import { useEffect, useState } from 'react';
import { Modal, Spin, Empty, Tag, message } from 'antd';
import { PlayCircleFilled } from '@ant-design/icons';
import PageLayout from '../components/PageLayout';
import { redPlaysAPI } from '../api';
import { isDirectVideoUrl } from '../sections/MediaSection';

interface FilmItem {
  id: number;
  title: string;
  category?: string;
  cover_url?: string;
  iframe_src?: string;
}

/** 首页影像 · 全集（P：与档案馆同级入口，由首页影像区「查看全部」进入） */
export default function FilmsPage() {
  const [items, setItems] = useState<FilmItem[] | null>(null);
  const [player, setPlayer] = useState<FilmItem | null>(null);

  useEffect(() => {
    redPlaysAPI.list({ category: '演出视频', limit: 50 })
      .then((res: any) => setItems(res.list || []))
      .catch(() => { message.error('影像加载失败'); setItems([]); });
  }, []);

  const list = items ?? [];
  const videoCount = list.filter((i) => i.iframe_src).length;

  return (
    <PageLayout title="首页影像 · 全集" background="#FAF7F2">
      <div className="page-heading">
        <h1 className="text-h1">戏台影像 · 全集</h1>
        <p className="text-body">
          首页影像专区收录的戏台演出与红色视频、实景图片（共 {list.length} 条 · 视频 {videoCount} · 图片 {list.length - videoCount}）。有视频可在线播放，仅有封面则为纯图片展示。
        </p>
      </div>

      {items === null ? (
        <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
      ) : list.length === 0 ? (
        <Empty description="暂无影像，请稍后查看" style={{ padding: 80 }} />
      ) : (
        <div className="film-grid">
          {list.map((f) => {
            const isVideo = !!f.iframe_src;
            return (
              <article
                key={f.id}
                className="film-card"
                role="button"
                tabIndex={0}
                onClick={() => setPlayer(f)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPlayer(f); } }}
              >
                <div
                  className="film-card-cover"
                  style={f.cover_url
                    ? { backgroundImage: `url(${f.cover_url})` }
                    : { background: 'linear-gradient(135deg,#A3232B,#C0392B)' }}
                >
                  {isVideo && <span className="film-card-play"><PlayCircleFilled /></span>}
                  {!isVideo && !f.cover_url && <span className="film-card-play"><PlayCircleFilled /></span>}
                </div>
                <div className="film-card-body">
                  <span className="film-card-title">{f.title}</span>
                  <span className="film-card-tag">
                    <Tag color={isVideo ? 'volcano' : 'green'} style={{ marginRight: 0 }}>
                      {isVideo ? '视频' : '纯图片'}
                    </Tag>
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* 播放/查看弹窗：视频 → R2 直链 <video> 或 B 站 iframe；无视频 → 纯图片 */}
      <Modal
        title={player?.title}
        open={!!player}
        onCancel={() => setPlayer(null)}
        footer={null}
        width={860}
        destroyOnClose
      >
        {player?.iframe_src ? (
          isDirectVideoUrl(player.iframe_src) ? (
            <video
              src={player.iframe_src}
              controls
              style={{ width: '100%', borderRadius: 8, background: '#000', aspectRatio: '16 / 9', objectFit: 'contain' }}
            />
          ) : (
            <div style={{ position: 'relative', paddingTop: '56.25%' }}>
              <iframe
                title={player.title}
                src={player.iframe_src}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                allowFullScreen
              />
            </div>
          )
        ) : player?.cover_url ? (
          <img
            src={player.cover_url}
            alt={player.title}
            style={{ width: '100%', maxHeight: '72vh', objectFit: 'contain', borderRadius: 8, background: '#000' }}
          />
        ) : (
          <div className="placeholder-note" style={{ margin: 0 }}>
            暂无内容（可填 B 站嵌入地址，或在素材库选 R2 视频 / 图片）。
          </div>
        )}
        <p style={{ color: '#888', marginTop: 12 }}>分类：{player?.category || '演出视频'}</p>
      </Modal>
    </PageLayout>
  );
}
